/**
 * What the crew knows (`/hq/areas/:slug/brief`, slice 6d spec §8.5; Paper
 * A5, A5b, A5c, A5d): an Area's brief, which anyone in the crew can change.
 * Sections changes a line at a time; Text (desktop) the whole file. The side
 * panel has Recent changes, All N versions, and an ask that starts a chat in
 * the Area. On a phone it's the list of lines and Change a fact's sheet.
 */
import { BackLink } from "@crew/ui/components/back-link";
import { Spinner } from "@crew/ui/components/spinner";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { StartComposer } from "../chats/start-composer";
import { useAreaPage, type Area } from "./api";
import { useBrief, useUndoBrief, type Brief } from "./brief-api";
import { SectionsView } from "./brief-sections";
import { useLineEditor } from "./use-line-editor";
import { BriefFooter, TextView } from "./brief-text";
import { RecentChanges, VersionsView } from "./brief-versions";
import { PointsTo, titlesOf } from "./mention-parts";
import { lengthLine } from "./brief";

type View = "sections" | "text" | "versions";

const MAIN =
  "flex w-full max-w-6xl flex-col gap-6 px-4 pt-6 pb-16 sm:px-6 md:flex-row md:items-start md:gap-12 md:px-12 md:pt-9";

function Header({ area, view, onView }: { area: Area; view: View; onView: (v: View) => void }) {
  return (
    <header className="flex flex-col gap-3">
      <BackLink href={`/areas/${area.slug}`}>{area.label}</BackLink>
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[30px] leading-9 font-normal tracking-tight text-foreground md:text-page">
            What the crew knows
          </h1>
          <p className="text-body text-muted-foreground">
            What the crew reads whenever it works on the {area.label.toLowerCase()}.
          </p>
        </div>
        <Tabs
          value={view === "text" ? "text" : "sections"}
          onValueChange={(v) => onView(v as View)}
          className="hidden shrink-0 md:flex"
        >
          <TabsList aria-label="View">
            <TabsTrigger value="sections">Sections</TabsTrigger>
            <TabsTrigger value="text">Text</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </header>
  );
}

/** Or just tell the crew: an ask that starts a chat in the Area. */
function TellTheCrew({ area }: { area: Area }) {
  const navigate = useNavigate();
  return (
    <section
      className="flex flex-col gap-3 rounded-xl bg-secondary/60 px-5 py-4 ring-1 ring-border"
      data-brief-tell
    >
      <h2 className="text-body font-medium text-foreground">Or just tell the crew</h2>
      <p className="text-meta text-muted-foreground">
        Say it in any {area.label.toLowerCase()} chat and the crew updates this for you.
      </p>
      <StartComposer
        size="md"
        placeholder="Tell the crew something"
        label={`Tell the crew about ${area.label}`}
        areaId={area.id}
        onStarted={(threadId) => navigate(`/chats/${threadId}`)}
      />
    </section>
  );
}

type Undo = { run: (version: number) => void; busy: boolean };

type BodyProps = {
  area: Area;
  brief: Brief;
  me: Me;
  undo: Undo;
  mobile: boolean;
  onView: (v: View) => void;
};

/** Facts, Notes and other lines, and the footer under them. */
function SectionsBody({ area, brief, me, undo, mobile, onView }: BodyProps) {
  const { file, recent } = brief;
  const ed = useLineEditor(area.id, file, me.workspace.timezone);
  return (
    <>
      <SectionsView
        file={file}
        area={area}
        titles={titlesOf(brief.mentions)}
        recent={recent}
        me={me}
        ed={ed}
        undo={undo}
        onText={mobile ? undefined : () => onView("text")}
      />
      <div className="hidden md:block">
        <BriefFooter length={file.length} version={file.version} saving={ed.busy || undo.busy} />
      </div>
      <PhoneFooter area={area} length={file.length} version={file.version} />
    </>
  );
}

const BODIES: Record<View, (props: BodyProps) => ReactNode> = {
  sections: SectionsBody,
  text: ({ area, brief }) => <TextView key={brief.file.id} areaId={area.id} file={brief.file} />,
  versions: ({ area, me, undo, onView }) => (
    <VersionsView areaId={area.id} me={me} undo={undo} onBack={() => onView("sections")} />
  ),
};

function Loaded({ area, brief, me }: { area: Area; brief: Brief; me: Me }) {
  const mobile = useIsMobile();
  const [chosen, setView] = useState<View>("sections");
  const view = mobile ? "sections" : chosen;
  const undoing = useUndoBrief(area.id);
  const undo = { run: (v: number) => undoing.mutate(v), busy: undoing.isPending };
  const Body = BODIES[view];
  return (
    <main data-brief-page={area.slug} data-version={brief.file.version} className={MAIN}>
      <div className="flex min-w-0 flex-1 flex-col gap-6 md:gap-8">
        <Header area={area} view={view} onView={setView} />
        <UndoProblem error={undoing.error} />
        <Body area={area} brief={brief} me={me} undo={undo} mobile={mobile} onView={setView} />
      </div>
      <aside className="hidden w-[300px] shrink-0 flex-col gap-4 md:flex">
        <RecentChanges
          recent={brief.recent}
          version={brief.file.version}
          me={me}
          undo={undo}
          onAll={() => setView("versions")}
        />
        {area.archived_at ? null : <TellTheCrew area={area} />}
        <PointsTo mentions={brief.mentions ?? []} me={me} />
      </aside>
    </main>
  );
}

/** "That line has changed since. Edit it instead.", from a refused Undo. */
function UndoProblem({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" data-brief-undo-error className="text-meta text-brick">
      {errorHint(error)}
    </p>
  );
}

/** A5b's footer: say it in a chat, and the count. */
function PhoneFooter({ area, length, version }: { area: Area; length: number; version: number }) {
  return (
    <div className="flex flex-col gap-1 md:hidden">
      <p className="text-meta text-muted-foreground">
        Or say it in any {area.label.toLowerCase()} chat, and the crew updates this.
      </p>
      <p className="text-xs text-hint tabular" data-brief-version={version}>
        {lengthLine(length)} · version {version}
      </p>
    </div>
  );
}

function Loading({ error }: { error: unknown }) {
  return (
    <main className={MAIN}>
      {error ? (
        <p className="text-body text-muted-foreground">{errorHint(error)}</p>
      ) : (
        <Spinner className="size-5 text-muted-foreground" />
      )}
    </main>
  );
}

function BriefOf({ area, me }: { area: Area; me: Me }) {
  const brief = useBrief(area.id);
  return brief.data ? (
    <Loaded area={area} brief={brief.data} me={me} />
  ) : (
    <Loading error={brief.error} />
  );
}

export function BriefPage({ slug, me }: { slug: string; me: Me }) {
  const page = useAreaPage(slug);
  return page.data ? <BriefOf area={page.data.area} me={me} /> : <Loading error={page.error} />;
}
